import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';

const seedLabel = (s) => `${s.combinedSeedsName || s.seedsName || 'Seed'} #${s.seedsID}`;

// Seeds that can replace this one in small-bag production (and the other way round:
// the link always works in both directions). Saved right away, no need to press Save.
// Rendered in edit mode only (needs an existing seedsID).
const SeedSubstitutesField = ({ seedsID }) => {
    const [items, setItems] = useState([]);       // [{ seedsID, name }]
    const [allSeeds, setAllSeeds] = useState([]);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(() => {
        if (!seedsID) return;
        api.get(`/api/seed-substitutes/${seedsID}`)
            .then(r => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => setItems([]));
    }, [seedsID]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get('/api/options/seeds').then(r => setAllSeeds(r.data.items || [])).catch(() => {});
    }, []);

    if (!seedsID) return null;

    const taken = new Set([String(seedsID), ...items.map(i => String(i.seedsID))]);
    const options = allSeeds.filter(s => !taken.has(String(s.seedsID)));

    // Picking a suggestion puts its full label into the input: that is the signal to add
    const handleSearch = async (value) => {
        setSearch(value);
        const seed = options.find(s => seedLabel(s) === value);
        if (!seed) return;
        setError('');
        try {
            await api.post(`/api/seed-substitutes/${seedsID}`, { substituteSeedsID: seed.seedsID });
            setSearch('');
            load();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add');
        }
    };

    const handleDelete = async (id) => {
        setError('');
        try {
            await api.delete(`/api/seed-substitutes/${seedsID}/${id}`);
            setItems(prev => prev.filter(i => i.seedsID !== id));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to remove');
        }
    };

    return (
        <div className="kit-field" style={{ gridColumn: '1 / -1' }}>
            <span className="kit-field__label"
                title="Bags of these seeds can be taken for small-bag production instead of this seed, and this seed instead of them">
                <i className="fa-solid fa-right-left" style={{ marginRight: 6, color: '#d97706' }}></i>
                Interchangeable Seeds
            </span>

            {items.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0 6px' }}>
                    {items.map(item => (
                        <span key={item.seedsID} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12,
                            padding: '3px 8px', fontSize: 12,
                        }}>
                            {item.name}
                            <span style={{ color: '#9ba3b0', fontSize: 10 }}>#{item.seedsID}</span>
                            <button type="button" onClick={() => handleDelete(item.seedsID)} title="Remove"
                                style={{ border: 'none', background: 'none', color: '#9ba3b0', cursor: 'pointer', padding: 0, fontSize: 11, lineHeight: 1 }}>
                                <i className="fas fa-times"></i>
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <input
                className="kit-field__input"
                list="seed-substitute-options"
                value={search}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Type a seed name and pick it from the list to add…"
            />
            <datalist id="seed-substitute-options">
                {options.map(s => <option key={s.seedsID} value={seedLabel(s)} />)}
            </datalist>
            {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 4 }}>⚠ {error}</div>}
        </div>
    );
};

export default SeedSubstitutesField;
